import React, {useMemo} from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkGemoji from 'remark-gemoji';
import remarkBreaks from 'remark-breaks';
import {parseTimestamps} from '@/modules/music_sniper/components/comments/comment_section';

type TimestampLinkProps = {
    timestamp: string;
    clickedTimestamp: (timestamp: string) => void;
};

const TimestampLink = (props: TimestampLinkProps) => {
    return (
        <a
            style={{
                display: 'inline-block',
                padding: '0.1em 0.2em',
                marginBottom: '0.2em',
                backgroundColor: 'var(--mantine-color-default-border)',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
                fontSize: 'var(--mantine-font-size-sm)',
                color: 'var(--mantine-color-text)',
            }}
            href='#'
            onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                props.clickedTimestamp(props.timestamp);
            }}
        >
            {props.timestamp}
        </a>
    );
};

type TimestampAwareMarkdownProps = {
    text: string;
    clickedTimestamp: (timestamp: string) => void;
    className?: string;
};

export const TimestampAwareMarkdown = (props: TimestampAwareMarkdownProps) => {
    return useMemo(() => {
        const markdownText = props.text.split(/(\s+)/).map(part => {
            if (part.trim() === '') {
                return part;
            }

            const timestamps = parseTimestamps(part);
            if (!timestamps.length) {
                return part;
            }

            const timestamp = timestamps[0];
            return `[${timestamp}](${timestamp})`;
        }).join('');

        return (
            <Markdown
                remarkPlugins={[remarkGfm, remarkGemoji, remarkBreaks]}
                className={props.className}
                components={{
                    a: ({children, node, ...markdownProps}) => {
                        const linkText = String(children).toString();

                        const timestamps = linkText ? parseTimestamps(linkText) : [];
                        if (!timestamps.length || timestamps[0] !== linkText) {
                            return (
                                <a
                                    {...markdownProps}
                                    target='_blank'
                                >
                                    {children}
                                </a>
                            );
                        }

                        return (
                            <TimestampLink
                                clickedTimestamp={props.clickedTimestamp}
                                timestamp={timestamps[0]}
                            />
                        );
                    },
                }}
            >
                {markdownText}
            </Markdown>
        );
    }, [props.text, props.clickedTimestamp, props.className]);
};